import { useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { Badge } from '../ui/badge'
import { Card, CardContent } from '../ui/card'
import { ParticipationLine, RoundResult } from './SittingTab'
import { useSitting } from './sitting-api'

/**
 * The room screen (spec 2026-10-05-09): the scrutin open now, the
 * participation and the results already closed, refreshed by the event
 * stream. Never a validity threshold, only who takes part.
 */
export function RoomScreen() {
    const { org, uid } = useParams({ from: '/$org/assemblies/$uid_/room' })
    const { t } = useTranslation('assemblies')
    const { data: sitting } = useSitting(org, uid)
    useBreadcrumbs([
        { title: t('title'), path: `/${org}/assemblies` },
        { title: sitting?.title ?? '…', path: `/${org}/assemblies/${uid}?tab=sitting` },
        { title: t('sitting.room') },
    ])
    if (!sitting) return <p className="text-sm text-muted-foreground">{t('loading')}</p>
    const open = sitting.rounds.find((r) => r.status === 'open')
    const closed = sitting.rounds.filter((r) => r.status === 'closed')
    return (
        <div className="space-y-6" data-testid="room-screen">
            <PageHeader
                icon={SECTION_ICONS.assemblies}
                title={sitting.title}
                titleAside={<Badge variant="outline" data-testid="room-status">{t(`sitting.status.${sitting.sitting?.status ?? 'running'}`)}</Badge>}
            />
            <ParticipationLine sitting={sitting} />
            <Card>
                <CardContent className="space-y-3 p-6" data-testid="room-current">
                    {open ? (
                        <>
                            <p className="text-sm uppercase text-muted-foreground">{t('sitting.now_voting')}</p>
                            <p className="text-3xl font-semibold">{open.number}. {open.title}</p>
                            <p className="whitespace-pre-line text-lg">{open.text}</p>
                        </>
                    ) : (
                        <p className="text-xl text-muted-foreground">{t('sitting.no_open_round')}</p>
                    )}
                </CardContent>
            </Card>
            {sitting.video_url && (
                <iframe
                    title={t('sitting.video')}
                    src={sitting.video_url}
                    allow="camera; microphone; fullscreen; display-capture"
                    className="h-[480px] w-full rounded-md border"
                    data-testid="room-video"
                />
            )}
            <ol className="space-y-2">
                {closed.map((r) => (
                    <li key={r.resolution_uid} className="rounded-md border p-3" data-testid={`room-round-${r.number}`}>
                        <p className="font-medium">{r.number}. {r.title}</p>
                        <RoundResult round={r} />
                    </li>
                ))}
            </ol>
        </div>
    )
}
